import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import type { FeedEvent } from '../../../app/package/base/service/feed/types';
import { LoginStatus } from '../../../app/package/base/service/login/login-domain';
import { TrackableEventAction } from '../../../app/package/base/service/tracking/tracking-service';
import type { GameDetails } from '../../base/package/api/package/gaming/gaming-domain';
import { I18n } from '../../base/package/services/package/i18n/i18n.de';
import { handleKeyboardTrigger } from '../../base/package/util/a11y';
import { reduceMotionStore } from '../../base/package/util/reduce-motion';
import { idleTick } from '../../base/package/util/render';
import { BIG_WIN, MEGA_WIN } from '../ui-win-card/ui-win-card';
import { UpdateDistributor } from './UpdateDistributor';
import { ArrayStateManager, type Column } from './UpdateHandler';
import style from './ui-activity-lane.css?inline';
import '../carousels/ui-custom-carousel';
import '../ui-column-orchestrator/ui-column-orchestrator';
import '../ui-hot-game/ui-hot-game';
import '../ui-rolling-number/ui-rolling-number';
import '../ui-screen-reader-only/ui-screen-reader-only';
import '../ui-tag/ui-tag';

const CName = 'ui-activity-lane';
const TRACKING_SOURCE = 'ActivityLane';

export type HotGameWin = {
  id: string;
  name: string;
  winAmount: number;
  multiplier: number;
  sticky: boolean;
};

export type HotGame = HotGameBE & {
  id: string;
};

export type HotGameBE = {
  gameId: string;
  isViral: boolean;
  activePlayers: number;
  rate: number;
  wins: HotGameWin[];
};

@customElement(CName)
export class UIActivityLane extends LitElement {
  static readonly styles = unsafeCSS(style);

  @state() private _cards: HotGame[] = [];
  @state() private _playerCounter = 0;
  @state() private _updateHandler: ArrayStateManager;
  @state() private _updateDistributor: UpdateDistributor;
  @state() private _hideLaneDueToCorruptData = false;
  @state() private _reduceMotion = reduceMotionStore.value;

  private _isTabActive = true;
  private _isInitialFeed = true;
  private readonly _boundFeedCallback = this._feedUpdateCallback.bind(this);
  private readonly _boundVisibilityChange = () => {
    this._isTabActive = document.visibilityState === 'visible';
  };

  private _reduceMotionCallback: ((v: boolean) => void) | undefined;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('visibilitychange', this._boundVisibilityChange);
    this._reduceMotionCallback = reduceMotionStore.subscribe((value) => {
      this._reduceMotion = value;
      if (this._updateHandler) {
        this._updateHandler.reduceMotion = value;
      }
    });
    this._subscribeToFeed();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('visibilitychange', this._boundVisibilityChange);
    if (this._reduceMotionCallback) {
      reduceMotionStore.unsubscribe(this._reduceMotionCallback);
    }
    this._unsubscribeFromFeed();
    this._updateHandler?.destroy();
  }

  render() {
    const currentArray: Column[] = this._updateHandler?.getCurrentState();

    if (this._hideLaneDueToCorruptData) return nothing;

    return html`
      <div class="activity-lane">
        <div class="activity-lane__header">
          <h2 class="activity-lane__title">${window.$app.$t.get(I18n.activityLane.title)}</h2>
          <div class="activity-lane__data ${classMap({ 'activity-lane__data--hidden': this._playerCounter === 0 })}">
            <ui-tag noAction colorVariant="vivid">
              <div class="activity-lane__chip">
                <ui-screen-reader-only>${this._playerCounter} ${window.$app.$t.get(I18n.activityLane.activePlayers)}</ui-screen-reader-only>
                <ui-rolling-number .value=${this._playerCounter} .reduceMotion=${this._reduceMotion}></ui-rolling-number> ${window.$app.$t.get(
                  I18n.activityLane.activePlayers,
                )}
              </div>
            </ui-tag>
          </div>
        </div>
        <div class="activity-lane__body">
          <ui-custom-carousel class="activity-lane__carousel" .padding=${'var(--page-padding-inline)'}>
            <ui-column-orchestrator
              .columns=${currentArray ?? []}
              .reduceMotion=${this._reduceMotion}
              .columnRenderFunction=${(column: HotGame, index: number) => this._renderHotGame(column, index)}
              @on-visible-columns-change=${this._onVisibleColumnsChange}
            >
            </ui-column-orchestrator>
          </ui-custom-carousel>
        </div>
      </div>
    `;
  }

  private _renderHotGame(card: HotGame, index: number) {
    return html`
        <ui-hot-game
          @click=${() => this._handleHotGameClick(card, index)}
          @keydown=${(ev: KeyboardEvent) => handleKeyboardTrigger(ev, this._handleHotGameClick.bind(this, card, index))}
          .playerNumber=${card.activePlayers}
          .fireRate=${card.rate}
          .gameId=${card.gameId}
          .wins=${card.wins}
          .isViral=${card.isViral}
          .reduceMotion=${this._reduceMotion}
          tabIndex='0'
        ></ui-hot-game>
    `;
  }

  private async _handleHotGameClick(card: HotGame, index: number): Promise<void> {
    try {
      const gameInfo = await window.$app.content.getGameInfo(card.gameId);
      this._trackGameSelection(card, gameInfo, index);
      this._updateVaixData(gameInfo.slug, index);
    } catch (err) {
      window.$app.logger.warn(`Error fetching game info '${card.gameId}'`, err);
    } finally {
      this._handleGameNavigation(card.gameId);
    }
  }

  private _trackGameSelection(card: HotGame, gameInfo: GameDetails, index: number): void {
    const trackingParams = {
      gameCategory: undefined,
      gameFilters: undefined,
      gameName: gameInfo.title,
      gamePosition: index + 1,
      gamePromo: 'image',
      gameProvider: gameInfo.getStudio(),
      gameSource: TRACKING_SOURCE,
      winType: this._getWinSizeForTracking(card.wins),
    };

    window.$app.trackingStore.next({ ...window.$app.trackingStore.value, ...trackingParams });
    window.$app.track.gamePlay({ ...trackingParams, eventAction: TrackableEventAction.GAME_SELECTED });
  }

  private _handleGameNavigation(gameId: string): void {
    if (window.$app.loginStore.value.loginStatus === LoginStatus.LOGGED_IN) {
      window.$app.router.navigateToGame(true, gameId);
    } else {
      window.$app.product.gotoLogin(TRACKING_SOURCE, `/game/real/${gameId}`);
    }
  }

  private _onVisibleColumnsChange(e: CustomEvent) {
    this._updateHandler.prioritizedIndexes = e.detail;
  }

  private _feedUpdateCallback(data: FeedEvent[]) {
    if (!this.isConnected) return;
    if (!data.length) {
      this._hideLaneDueToCorruptData = true;
      return;
    }

    if (!this._isTabActive) return; // ignore update if tab is inactive

    const initialFeed = data[0]?.data;

    this._playerCounter = initialFeed?.totalPlayers ?? 0;
    const initialGames = initialFeed?.games ?? [];

    if (this._updateDistributor) {
      this._updateDistributor.updateInput(initialGames);
    } else {
      this._updateDistributor = new UpdateDistributor(initialGames);
    }

    this._updateDistributor.startRotation(async (cards: HotGameBE[], updateInterval: number) => {
      if (!this.isConnected) return;

      if (!this._updateHandler) {
        this._cards = this._mapGames(cards);
        this._updateHandler = new ArrayStateManager(this._cards, {
          requestUpdateCallback: this.requestUpdate.bind(this),
          reduceMotion: this._reduceMotion,
        });
      }

      await this._updateCards(cards, updateInterval);
    }, this._isInitialFeed);
  }

  private _subscribeToFeed() {
    if (!window.$app.activityFeedService) {
      // Service is created in Application.initialize() after idleTick —
      // defer one idle tick to align with that timing.
      idleTick().then(() => {
        if (this.isConnected && window.$app.activityFeedService) {
          this._subscribeToFeed();
        }
      });
      return;
    }

    this._hideLaneDueToCorruptData = window.$app.activityFeedService.wasDisconnectedDueToInvalidData;
    if (this._hideLaneDueToCorruptData) {
      return;
    }

    window.$app.activityFeedService.connect();

    const lastState = window.$app.activityFeedService.getLastState();

    if (lastState?.length) {
      this._feedUpdateCallback(lastState);
    }

    window.$app.activityFeedService.subscribe(this._boundFeedCallback);
  }

  private _unsubscribeFromFeed() {
    if (!window.$app.activityFeedService) return;
    window.$app.activityFeedService.unsubscribe(this._boundFeedCallback);
    window.$app.activityFeedService.disconnect();
    this._updateDistributor?.stopRotation();
  }

  private _mapGames(games: HotGameBE[]): HotGame[] {
    return games.map((game) => ({
      ...game,
      id: game.gameId,
    }));
  }

  private async _updateCards(games: HotGameBE[], updateInterval: number) {
    this._isInitialFeed = false;
    this._cards = this._mapGames(games);

    await this._updateHandler.update(this._cards, updateInterval);
  }

  private _getWinSizeForTracking(wins: HotGameWin[]): 'normal' | 'mega' | 'big' {
    const foundWin = wins.map((win) => win.winAmount).find((win) => win > BIG_WIN);
    if (foundWin === undefined) return 'normal';
    if (foundWin > MEGA_WIN) return 'mega';
    return 'big';
  }

  private _updateVaixData(gameSlug: string, position: number) {
    window.$app.vaix.updateVaixData({
      categorySlug: 'activity-lane',
      gameSlug,
      page: 'lobby',
      position,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: UIActivityLane;
  }
}
